import { type ReactNode, useId, useMemo, useState } from 'react';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { ScrollEdge } from '../ScrollEdge/ScrollEdge';
import { SearchField } from '../SearchField/SearchField';

/**
 * A FILTERING LIST: a search box over a set of options, one of which gets chosen.
 *
 * THE ONE NEW PRIMITIVE THIS PRODUCT NEEDED (workflow-builder.md D37, red-team M4), and it
 * is the material D18's whole argument rests on. That decision - values are a PICKER, never
 * a template language - is justified by "a picker is better for a non-engineer", and the
 * repo had nothing to pick WITH: `SelectMenu` is a plain Radix Select with no search prop,
 * and there is no Combobox and no DatePicker anywhere in `src/ui/`. On a forty-field object
 * the field gate, the value-source field list and the assignee list would each have been an
 * unfiltered scroll column inside a 375px bottom sheet.
 *
 * AN INPUT PLUS A FILTERED `<ul>`, and nothing else. Radix is not needed here, so INV-14
 * costs this component nothing: there is no floating layer, no portal and no focus trap,
 * because a PickerList lives INSIDE a sheet page that already owns all three.
 *
 * IT IS NOT A `<select>` EITHER. The options carry a secondary line (a field's type, a
 * record's object) and a chosen state that has to survive a re-filter, neither of which a
 * native control renders - and on a phone a native picker would cover the sheet it was
 * opened from.
 */
export interface PickerListOption {
  readonly value: string;
  readonly label: string;
  /** One muted line under the label: a field's type, a record's object, a role. */
  readonly hint?: string | undefined;
  /** Rendered before the label. A kind icon, never decoration. */
  readonly leading?: ReactNode | undefined;
  readonly disabled?: boolean | undefined;
}

export interface PickerListProps {
  readonly options: readonly PickerListOption[];
  readonly value?: string | undefined;
  readonly onSelect: (value: string) => void;
  /** Required: a search box with no name is a box nobody knows the scope of. */
  readonly ariaLabel: string;
  readonly placeholder?: string | undefined;
  /**
   * Below this many options the search box is hidden: a filter over four things is a
   * control that costs a tap and saves none.
   */
  readonly searchFrom?: number | undefined;
  /** Shown in place of the list when nothing matches. A sentence, never "No results". */
  readonly emptyText?: string | undefined;
  /**
   * THE CALLER'S OWN SEARCH FIELD IS OVER THIS LIST (wave 4, C3; ledger P7-W4-2's open item).
   *
   * The edge answers to a fixed element above the rows, and normally that element is this
   * primitive's own search field - so it appears with it. `RunNowDialog` is the one caller
   * that draws the field ITSELF, outside the component, and hides this one with
   * `searchFrom: Infinity`; the rows still pass under something that holds still, and nothing
   * in here can see it. The prop is that fact, stated by the caller.
   *
   * WAVE 5, C1-1: IT TURNS THE BOUND ON, AND ONLY THE BOUND. The effect now sits on the
   * field the rows pass under, so a component that does not draw that field cannot mount it
   * either; the band for a caller-drawn field belongs to the caller.
   */
  readonly bounded?: boolean | undefined;
  readonly className?: string | undefined;
}

const SEARCH_FROM = 8;

export function PickerList({
  options,
  value,
  onSelect,
  ariaLabel,
  placeholder = 'Search',
  searchFrom = SEARCH_FROM,
  emptyText = 'Nothing here matches what you typed.',
  bounded,
  className,
}: PickerListProps) {
  const [query, setQuery] = useState('');
  const listId = useId();
  /**
   * THE SEARCH FIELD IS WHAT THE EDGE ANSWERS TO (wave 4, C2-20, owner-94). The rows scroll
   * under a field that holds still, which is the floating element the HIG's rule names -
   * "Only use a scroll edge effect when a scroll view is behind floating interface
   * elements" (hig:
   * https://developer.apple.com/design/human-interface-guidelines/scroll-views) - so the
   * edge appears with the search field and never without it. A picker lives inside a sheet
   * page, so the ground is the chrome it is drawn on.
   */
  const searchable = options.length >= searchFrom;
  /** The edge follows the FIXED ELEMENT, which is this component's field or the caller's. */
  const withEdges = bounded ?? searchable;

  const matches = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === '') return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(needle) ||
        (option.hint ?? '').toLowerCase().includes(needle),
    );
  }, [options, query]);

  /**
   * THE SCROLLER IS THE BOUNDED BOX AND THE FIELD IS PINNED INSIDE IT (wave 5 round 1,
   * C1-1), which is `ui/OptionList`'s construction and the same reasoning: the effect is
   * bounded by a REAL host, the host is the element the rows pass under, and a pinned
   * element holds flow space, so the first option is sharp at rest with no inset declared
   * (hig: https://developer.apple.com/design/human-interface-guidelines/scroll-views; doc:
   * https://developer.apple.com/documentation/uikit/uiscrollview/contentinsetadjustmentbehavior).
   * Wave 4 built the bound, the inset and the bands out of `--scroll-edge-overhang`, which
   * A deleted (f537fcf), so all three read as nothing on main.
   *
   * WHERE THE CALLER DRAWS THE FIELD, THIS PRIMITIVE DRAWS NO BAND. `RunNowDialog` hides
   * the field with `searchFrom: Infinity` and keeps its own above the list, because it
   * queries the server; the element the rows pass under is therefore not in here and there
   * is nothing for this component to mount the effect ON. `bounded` still buys the bound,
   * and the band is that caller's to mount on its own field.
   */
  const pinned = withEdges && searchable;

  return (
    <div className={cx('flex min-h-0 flex-col gap-2', className)}>
      {/* The search shell rather than a bare input, so the capsule is the kit's whole
          construction and not its fill alone (wave 5 round 5, correction C6-1). */}
      {searchable && !pinned && (
        <SearchField
          context="content"
          label={ariaLabel}
          placeholder={placeholder}
          value={query}
          aria-controls={listId}
          onChange={(event) => setQuery(event.target.value)}
        />
      )}

      {/*
        THE FIELD OUTLIVES ITS OWN RESULT, and it has to: it is INSIDE the scroller now, so
        unmounting the scroller when nothing matches takes the only control that can clear
        the query with it and the person is left in a dead end they can only leave by
        closing the sheet. The empty line goes where the rows were, under the field that put
        it there - which is also what the sentence is about. hig:
        https://developer.apple.com/design/human-interface-guidelines/searching - "if a
        search returns no results, display an explanation" beside the field that ran it.
      */}
      <div
        className={cx(
          '-mx-1 flex min-h-0 flex-col px-1',
          withEdges ? 'max-h-[calc(20rem_+_var(--touch-target-min))] overflow-y-auto' : '',
        )}
        {...(withEdges ? { 'data-bounded-list': 'chrome' } : {})}
      >
        {pinned && (
          <div className="sticky top-0 isolate">
            <ScrollEdge host="picker-list" style="soft" ground="chrome" />
            <SearchField
              context="content"
              label={ariaLabel}
              placeholder={placeholder}
              value={query}
              aria-controls={listId}
              onChange={(event) => setQuery(event.target.value)}
            />
          </div>
        )}
        {matches.length === 0 ? (
          <p className="px-1 py-6 text-center text-sm text-ink-muted">{emptyText}</p>
        ) : (
          <ul
            id={listId}
            aria-label={ariaLabel}
            /**
             * SCROLLS ITSELF rather than growing the sheet. A sheet is capped at 92dvh and a
             * list that pushed past it would take its own footer off the screen - so where
             * this list is not bounded, the `<ul>` keeps the scroller it has always had.
             */
            className={cx('min-h-0', withEdges ? '' : '-mx-1 max-h-80 overflow-y-auto px-1')}
          >
            {matches.map((option) => {
              const chosen = option.value === value;
              return (
                <li key={option.value}>
                  <button
                    type="button"
                    disabled={option.disabled === true}
                    aria-pressed={chosen}
                    onClick={() => onSelect(option.value)}
                    className={cx(
                      'pressable touch-target flex w-full items-center gap-2.5 rounded-control px-2.5 text-left',
                      'hover:bg-surface-hover disabled:opacity-50',
                    )}
                  >
                    {/*
                    The tick reserves its gutter whether or not it is showing, so choosing an
                    option does not shift every label one column to the right.
                  */}
                    <span className="flex w-4 shrink-0 items-center justify-center text-accent">
                      {chosen && <Icon name="check" size="0.875rem" />}
                    </span>
                    {option.leading !== undefined && (
                      <span className="shrink-0 text-ink-muted">{option.leading}</span>
                    )}
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm text-ink">{option.label}</span>
                      {option.hint !== undefined && (
                        <span className="truncate text-2xs text-ink-muted">{option.hint}</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
